/**
 * The email lifecycle (pending → processing → processed | failed) and how a
 * sender is shown, shared by the Email Queue and the email page so the two
 * always agree on colours, labels and avatars.
 */
import { Clock, CheckCircle2, Inbox, Loader2, Mail, XCircle } from 'lucide-react'

// Each status carries its own colour set:
//   chip  — icon tile on the overview card
//   bar   — share bar and row accent
//   badge — the status pill on a row
//   ring  — the selected overview card
//   glow  — "r g b" for the card's cursor spotlight (.collection-card)
export const STATUS_META = {
  '': {
    label: 'All emails', icon: Inbox,
    chip: 'bg-navy-50 text-navy-600 dark:bg-navy-500/15 dark:text-navy-300',
    bar: 'bg-navy-500',
    badge: 'bg-gray-50 text-gray-700 ring-gray-500/15 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600/30',
    ring: 'ring-navy-500/70 dark:ring-navy-400/60',
    tint: 'from-navy-50/80 dark:from-navy-500/10',
    glow: 'var(--navy-500-eff)',
  },
  pending: {
    label: 'Queued', icon: Clock,
    chip: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
    bar: 'bg-amber-400 dark:bg-amber-400/90',
    badge: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20',
    ring: 'ring-amber-400/80 dark:ring-amber-400/50',
    tint: 'from-amber-50/80 dark:from-amber-500/10',
    glow: '245 158 11',
  },
  processing: {
    label: 'Processing', icon: Loader2, spin: true,
    chip: 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300',
    bar: 'bg-sky-500 dark:bg-sky-400',
    badge: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20',
    ring: 'ring-sky-500/70 dark:ring-sky-400/50',
    tint: 'from-sky-50/80 dark:from-sky-500/10',
    glow: '14 165 233',
  },
  processed: {
    label: 'Processed', icon: CheckCircle2,
    chip: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
    bar: 'bg-emerald-500 dark:bg-emerald-400',
    badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20',
    ring: 'ring-emerald-500/70 dark:ring-emerald-400/50',
    tint: 'from-emerald-50/80 dark:from-emerald-500/10',
    glow: '16 185 129',
  },
  failed: {
    label: 'Failed', icon: XCircle,
    chip: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300',
    bar: 'bg-rose-500 dark:bg-rose-400',
    badge: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20',
    ring: 'ring-rose-500/70 dark:ring-rose-400/50',
    tint: 'from-rose-50/80 dark:from-rose-500/10',
    glow: '244 63 94',
  },
}

export const STATUS_ORDER = ['', 'pending', 'processing', 'processed', 'failed']

export const getStatusMeta = (status) => STATUS_META[status] || {
  ...STATUS_META[''],
  icon: Mail,
  label: status ? status.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : 'Unknown',
}

// Quiet refresh while anything is queued or processing.
export const LIVE_INTERVAL_MS = 15000

/** "Jane Smith <jane@acme.com>" → name + address; a bare address is both. */
export function parseSender(raw) {
  const value = (raw || '').trim()
  const match = /^"?([^"<]*?)"?\s*<([^>]+)>$/.exec(value)
  if (match) return { name: match[1].trim() || match[2], address: match[2] }
  return { name: value || 'Unknown sender', address: value.includes('@') ? value : '' }
}

export function initialsFor({ name, address }) {
  const base = name && name !== address ? name : (address || name || '').split('@')[0]
  const parts = base.split(/[\s._\-+]+/).filter(Boolean)
  if (!parts.length) return '?'
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase()
}

export const AVATAR_TONES = [
  'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
]

// The same sender always gets the same colour, so repeat senders stand out.
export function avatarTone(key) {
  let hash = 0
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length]
}
